import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  type BuyerSubmissionStatus,
  type SetBuyerSubmissionStatusPayload,
} from "@/api/buyers";
// Modal is a staff-shell component, not part of the shared portal kit.
import { Modal } from "@/components/ui/Modal";
import { Button, StatusBadge, Textarea, useToast } from "@/design-system/components/ui";
import { buyerKeys, buyerSubmissionKeys } from "@/hooks/buyers/buyerKeys";
import { useAuth } from "@/hooks/useAuth";
import { SUBMISSION_STATUSES, buyerLabel } from "./buyerFilters";
import { submissionStatusTone } from "./buyerListBits";

const SELECT_CLASS =
  "w-full rounded-sm border border-gray-mid bg-white-pure px-space-2 py-[6px] text-small text-ink";

/**
 * The five terminal statuses, which the server requires a reason for.
 *
 * Mirrors <c>BuyerSubmissionProgress</c>'s TerminalRank set. Kept as an explicit
 * list rather than inferred from the badge tone: a display colour is not an
 * authorization fact, and keying a required field off one would break silently
 * the first time a tone is restyled.
 */
const TERMINAL_STATUSES = new Set<BuyerSubmissionStatus>([
  "DeclinedByBuyer",
  "NoResponse",
  "RejectedByBroker",
  "Passed",
  "Dead",
]);

/**
 * Which milestone a status change stamps, server-side.
 *
 * <p>🔴 Shown rather than hidden because the stamp is a SIDE EFFECT of the
 * status move and there is no separate control for it — a specialist choosing
 * "Under contract" is also, invisibly, recording the date it went under
 * contract. Stamping is idempotent (<c>??=</c>), so revisiting a status later
 * never overwrites the earlier, truer date.</p>
 *
 * <p>Statuses absent from this map stamp nothing: the terminal five did not
 * reach a milestone by dying, and the package pair are stamped by the package
 * actions instead.</p>
 */
const MILESTONE_FOR: Partial<Record<BuyerSubmissionStatus, string>> = {
  BuyerApproved: "Buyer approved",
  BrokerResponded: "Broker responded",
  // 🔴 CIM ONLY. "Info received" covers the CIM and/or the financials, and the
  // server deliberately stamps just the CIM — claiming both would record
  // documents nobody has seen.
  InfoReceived: "CIM received",
  CallMeeting: "Call / meeting",
  OfferLoi: "Offer / LOI",
  UnderContract: "Under contract",
  ClosedPurchased: "Closed",
};

/**
 * 🔴 <b>EscalatedToDeal is not settable by hand.</b> It is written only by the
 * escalate action, in the same save that stamps the deal pointer, so the status
 * can never exist without a deal behind it. The server throws on it; offering it
 * here would be offering a guaranteed error.
 */
const SELECTABLE_STATUSES = SUBMISSION_STATUSES.filter((s) => s !== "EscalatedToDeal");

/**
 * Move a submission's status — the spine of the whole record.
 *
 * Self-gates on `Submissions.Manage` and on the row's server-resolved
 * `canEdit`, so it renders as a plain badge for a reader and for a specialist
 * who does not own the parent buyer.
 */
export function SubmissionStatusControl({
  submissionId,
  status,
  canEdit,
}: {
  submissionId: string;
  status: BuyerSubmissionStatus;
  canEdit: boolean;
}) {
  const { hasPermission } = useAuth();
  const [open, setOpen] = useState(false);

  const editable = canEdit && hasPermission("Submissions.Manage");

  /*
    🔴 EscalatedToDeal is ABSORBING — nothing moves a submission back out of it,
    and the server now refuses to.

    The guard used to cover only one side of the rule: EscalatedToDeal was
    refused as a TARGET ("a submission becomes escalated by being escalated")
    while the CURRENT status went unchecked, so an already-escalated submission
    could be moved anywhere. EscalatedDealSubmissionId is never cleared, so the
    record would still read through to a live DealSubmission while claiming to
    be, say, Passed — and the buyer's derived furthest-along badge would report a
    buyer with a Phoenix deal in flight as a dead one.

    Un-escalating is not a status move; it is a decision about the DEAL, and it
    belongs to whatever surface can also resolve what happens to the deal. So
    there is no override here and no reason field that would unlock one — the
    control is simply not offered, and the badge carries the explanation the
    server would otherwise have delivered as a 400 after the click.
  */
  if (status === "EscalatedToDeal") {
    return (
      <StatusBadge
        tone={submissionStatusTone(status)}
        hint="Settled by the escalation. Work the deal instead."
        data-testid="submission-status-absorbing"
      >
        {buyerLabel(status)}
      </StatusBadge>
    );
  }

  if (!editable) {
    return (
      <StatusBadge tone={submissionStatusTone(status)}>{buyerLabel(status)}</StatusBadge>
    );
  }

  return (
    <>
      <div className="flex items-center gap-space-2">
        <StatusBadge tone={submissionStatusTone(status)}>{buyerLabel(status)}</StatusBadge>
        <Button
          size="sm"
          variant="secondary"
          data-testid="submission-status-change"
          onClick={() => setOpen(true)}
        >
          Change status
        </Button>
      </div>

      <StatusModal
        submissionId={submissionId}
        current={status}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}

function StatusModal({
  submissionId,
  current,
  open,
  onClose,
}: {
  submissionId: string;
  current: BuyerSubmissionStatus;
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [next, setNext] = useState<BuyerSubmissionStatus>(current);
  const [reason, setReason] = useState("");

  const terminal = TERMINAL_STATUSES.has(next);
  const milestone = MILESTONE_FOR[next];
  const changed = next !== current;
  const reasonMissing = terminal && reason.trim().length === 0;

  const setStatus = useMutation({
    mutationFn: (body: SetBuyerSubmissionStatusPayload) =>
      buyerSubmissionsApi.setStatus(submissionId, body),
    onSuccess: () => {
      // The row moves in the list AND the detail re-reads, so the whole prefix
      // goes — the two must never disagree about where this submission stands.
      void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
      // The buyer's DERIVED furthest-along stage (L5) is computed from its
      // submissions, so a status move restages the buyer too.
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Status updated.");
      close();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function close() {
    setNext(current);
    setReason("");
    onClose();
  }

  return (
    <Modal open={open} onClose={close} title="Change the submission's status">
      <div className="grid gap-space-4">
        <div className="space-y-space-1">
          <label htmlFor="submission-status" className="block text-caption text-gray-mid">
            Status
          </label>
          <select
            id="submission-status"
            data-testid="submission-status-select"
            className={SELECT_CLASS}
            value={next}
            onChange={(e) => setNext(e.target.value as BuyerSubmissionStatus)}
          >
            {SELECTABLE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {buyerLabel(s)}
              </option>
            ))}
          </select>
        </div>

        {/* The stamp is invisible otherwise, and there is no other control that
            records these dates. */}
        {changed && milestone && (
          <p className="text-caption text-gray-mid m-0" data-testid="submission-milestone-note">
            This also stamps the &ldquo;{milestone}&rdquo; milestone, if it is not
            already stamped.
          </p>
        )}

        {terminal && (
          <div className="space-y-space-1">
            <label
              htmlFor="submission-status-reason"
              className="block text-caption text-gray-mid"
            >
              Why is it closing out? (required)
            </label>
            <Textarea
              id="submission-status-reason"
              data-testid="submission-status-reason"
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. the buyer walked after seeing the financials"
            />
            {/* Not a formality — it is the question the pipeline report exists
                to answer, and the server refuses without it. */}
            <p className="text-caption text-gray-mid m-0">
              &ldquo;Why did this die&rdquo; is what the pipeline report is read for.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-space-2">
          <Button variant="text" onClick={close}>
            Cancel
          </Button>
          <Button
            data-testid="submission-status-submit"
            disabled={!changed || reasonMissing || setStatus.isPending}
            onClick={() =>
              setStatus.mutate({
                status: next,
                reason: reason.trim() || null,
              })
            }
          >
            {setStatus.isPending ? "Saving…" : "Update status"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
